<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Growth Monitoring</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Step 1: Select Child -->
          <div v-if="step === 1">
            <div class="mb-4">
              <h6 class="mb-3">Select Child for Growth Monitoring</h6>

              <!-- Search -->
              <div class="search-box mb-3">
                <div class="position-relative">
                  <input v-model="searchQuery" @input="searchChildren" type="text"
                         class="form-control" placeholder="Search by child name, CBHI number, or household...">
                  <i class="bx bx-search-alt search-icon"></i>
                </div>
              </div>

              <!-- Children Due for Monitoring -->
              <div v-if="dueForMonitoring.length > 0" class="mb-4">
                <h6 class="text-muted mb-2">Due for Growth Monitoring</h6>
                <div class="list-group">
                  <div v-for="child in dueForMonitoring" :key="child.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedChild?.member_id === child.member_id}"
                       @click="selectChild(child)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ child.first_name }} {{ child.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Age: {{ getChildAge(child.date_of_birth) }} •
                          {{ child.household_number }}
                        </p>
                        <small :class="getNutritionalStatusClass(child.last_nutritional_status)">
                          Last Status: {{ child.last_nutritional_status || 'Not assessed' }}
                          <span v-if="child.last_measurement_date" class="text-muted">
                            ({{ getDaysSince(child.last_measurement_date) }} days ago)
                          </span>
                        </small>
                      </div>
                      <div>
                        <span class="badge bg-warning">Due</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Results -->
              <div v-if="searchResults.length > 0">
                <h6 class="text-muted mb-2">Search Results</h6>
                <div class="list-group">
                  <div v-for="child in searchResults" :key="child.member_id"
                       class="list-group-item list-group-item-action"
                       :class="{'active': selectedChild?.member_id === child.member_id}"
                       @click="selectChild(child)">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">{{ child.first_name }} {{ child.surname }}</h6>
                        <p class="mb-0 text-muted">
                          Age: {{ getChildAge(child.date_of_birth) }} •
                          {{ child.cbhi_number || 'No CBHI' }}
                        </p>
                      </div>
                      <div>
                        <span v-if="child.enrollment_type === 'cbhi_member'"
                              class="badge bg-success">CBHI</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Results -->
              <div v-else-if="searchQuery && !loading" class="text-center py-4">
                <p class="text-muted">No children found</p>
              </div>
            </div>
          </div>

          <!-- Step 2: Previous Measurements & Current Assessment -->
          <div v-else-if="step === 2">
            <div class="mb-4">
              <!-- Child Info -->
              <div class="alert alert-info">
                <div class="d-flex justify-content-between">
                  <div>
                    <h6 class="alert-heading">{{ selectedChild.first_name }} {{ selectedChild.surname }}</h6>
                    <p class="mb-0">
                      Age: {{ getChildAge(selectedChild.date_of_birth) }}
                      ({{ getAgeInMonths(selectedChild.date_of_birth) }} months)<br>
                      Gender: {{ selectedChild.gender === 'M' ? 'Male' : 'Female' }} •
                      CBHI: {{ selectedChild.cbhi_number || 'Not enrolled' }}
                    </p>
                  </div>
                  <div class="text-end">
                    <button @click="previousStep" class="btn btn-sm btn-secondary">
                      <i class="bx bx-arrow-back"></i> Change Child
                    </button>
                  </div>
                </div>
              </div>

              <!-- Growth History Chart -->
              <div v-if="growthHistory.length > 0" class="mb-4">
                <h6 class="mb-3">Growth History</h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Date</th>
                      <th>Age</th>
                      <th>Weight (kg)</th>
                      <th>Height (cm)</th>
                      <th>MUAC (cm)</th>
                      <th>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="record in growthHistory" :key="record.monitoring_id"
                        :class="{'table-danger': record.nutritional_status === 'SAM',
                                   'table-warning': record.nutritional_status === 'MAM'}">
                      <td>{{ formatDate(record.measurement_date) }}</td>
                      <td>{{ record.age_months }}m</td>
                      <td>{{ record.weight }}</td>
                      <td>{{ record.height || '-' }}</td>
                      <td>{{ record.muac || '-' }}</td>
                      <td>
                          <span :class="'badge ' + getNutritionalBadgeClass(record.nutritional_status)">
                            {{ record.nutritional_status }}
                          </span>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Current Measurements -->
              <h6 class="mb-3">Current Measurements</h6>
              <div class="row">
                <!-- Weight -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">Weight (kg) <span class="text-danger">*</span></label>
                  <div class="input-group">
                    <input v-model.number="measurements.weight" type="number" step="0.1"
                           class="form-control" :class="{'is-invalid': weightError}"
                           placeholder="0.0" min="0" max="50" required>
                    <span class="input-group-text">kg</span>
                  </div>
                  <div v-if="weightError" class="invalid-feedback">{{ weightError }}</div>
                </div>

                <!-- Height -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">Height/Length (cm)</label>
                  <div class="input-group">
                    <input v-model.number="measurements.height" type="number" step="0.1"
                           class="form-control" placeholder="0.0" min="0" max="200">
                    <span class="input-group-text">cm</span>
                  </div>
                </div>

                <!-- MUAC -->
                <div class="col-md-4 mb-3">
                  <label class="form-label">MUAC (cm)</label>
                  <div class="input-group">
                    <input v-model.number="measurements.muac" type="number" step="0.1"
                           class="form-control" :class="{'is-invalid': muacError}"
                           placeholder="0.0" min="0" max="30">
                    <span class="input-group-text">cm</span>
                  </div>
                  <div v-if="muacError" class="invalid-feedback">{{ muacError }}</div>
                </div>
              </div>

              <!-- Nutritional Status Indicator -->
              <div v-if="nutritionalStatus" class="alert"
                   :class="{'alert-danger': nutritionalStatus === 'SAM',
                            'alert-warning': nutritionalStatus === 'MAM',
                            'alert-success': nutritionalStatus === 'normal'}">
                <h6 class="alert-heading">
                  <i :class="'bx ' + getNutritionalIcon(nutritionalStatus)"></i>
                  Nutritional Status: {{ getNutritionalStatusLabel(nutritionalStatus) }}
                </h6>
                <p class="mb-0" v-if="nutritionalStatus === 'SAM'">
                  Severe Acute Malnutrition detected. Immediate referral required.
                </p>
                <p class="mb-0" v-else-if="nutritionalStatus === 'MAM'">
                  Moderate Acute Malnutrition detected. Nutritional support needed.
                </p>
              </div>

              <!-- Edema Check -->
              <div class="mb-3">
                <div class="form-check">
                  <input v-model="measurements.edema" class="form-check-input" type="checkbox"
                         id="edemaCheck">
                  <label class="form-check-label" for="edemaCheck">
                    Bilateral pitting edema present
                  </label>
                </div>
                <small class="text-muted">Check if edema is present on both feet</small>
              </div>

              <!-- Services Provided -->
              <h6 class="mb-3">Services Provided</h6>
              <div class="row">
                <div class="col-md-4">
                  <div class="form-check">
                    <input v-model="services.vitaminA" class="form-check-input" type="checkbox"
                           id="vitaminACheck">
                    <label class="form-check-label" for="vitaminACheck">
                      Vitamin A supplementation
                    </label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input v-model="services.deworming" class="form-check-input" type="checkbox"
                           id="dewormingCheck">
                    <label class="form-check-label" for="dewormingCheck">
                      Deworming medication
                    </label>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="form-check">
                    <input v-model="services.counseling" class="form-check-input" type="checkbox"
                           id="counselingCheck">
                    <label class="form-check-label" for="counselingCheck">
                      Feeding counseling
                    </label>
                  </div>
                </div>
              </div>

              <!-- Referral Decision -->
              <div v-if="nutritionalStatus === 'SAM' || nutritionalStatus === 'MAM'"
                   class="alert alert-warning mt-3">
                <h6 class="alert-heading">
                  <i class="bx bx-transfer"></i> Referral Recommended
                </h6>
                <div class="form-check">
                  <input v-model="referralNeeded" class="form-check-input" type="checkbox"
                         id="referralCheck">
                  <label class="form-check-label" for="referralCheck">
                    Create referral for malnutrition treatment
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button v-if="step === 1" type="button" class="btn btn-primary"
                  @click="loadChildData" :disabled="!selectedChild">
            Next <i class="bx bx-chevron-right"></i>
          </button>

          <div v-else-if="step === 2">
            <button type="button" class="btn btn-secondary me-2" @click="previousStep">
              <i class="bx bx-chevron-left"></i> Back
            </button>
            <button type="button" class="btn btn-success" @click="recordMeasurements"
                    :disabled="!measurements.weight || submitting">
              <span v-if="submitting">
                <span class="spinner-border spinner-border-sm me-1"></span> Recording...
              </span>
              <span v-else>
                <i class="bx bx-check"></i> Record Measurements
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "GrowthMonitoringModal",
  props: {
    preselectedChild: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      step: 1,
      loading: false,
      submitting: false,

      // Child selection
      searchQuery: '',
      searchResults: [],
      dueForMonitoring: [],
      selectedChild: null,

      // Growth data
      growthHistory: [],

      // Current measurements
      measurements: {
        weight: null,
        height: null,
        muac: null,
        edema: false
      },

      // Services
      services: {
        vitaminA: false,
        deworming: false,
        counseling: false
      },

      // Referral
      referralNeeded: false,

      // Validation
      weightError: '',
      muacError: ''
    };
  },

  computed: {
    nutritionalStatus() {
      if (!this.measurements.weight) return null;

      // Check for edema first - indicates SAM
      if (this.measurements.edema) return 'SAM';

      // Check MUAC if available
      if (this.measurements.muac) {
        if (this.measurements.muac < 11.5) return 'SAM';
        if (this.measurements.muac < 12.5) return 'MAM';
      }

      // Additional weight-for-age or weight-for-height calculations would go here
      // This is simplified - actual implementation would use WHO growth standards

      return 'normal';
    }
  },

  watch: {
    'measurements.weight'(value) {
      this.validateWeight();
    },
    'measurements.muac'(value) {
      this.validateMUAC();
    }
  },

  mounted() {
    if (this.preselectedChild) {
      this.selectedChild = this.preselectedChild;
      this.loadChildData();
    } else {
      this.loadDueChildren();
    }
  },

  methods: {
    async loadDueChildren() {
      this.loading = true;

      const data = {
        "function": "getChildrenDueForGrowthMonitoring"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.dueForMonitoring = res.data.data.children || [];
        }
      } catch (e) {
        console.error("Failed to load due children:", e);
      } finally {
        this.loading = false;
      }
    },

    async searchChildren() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = [];
        return;
      }

      this.loading = true;

      const data = {
        "function": "searchMembers",
        "searchTerm": this.searchQuery,
        "searchType": "under5"
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.searchResults = res.data.data.members || [];
        }
      } catch (e) {
        console.error("Failed to search children:", e);
      } finally {
        this.loading = false;
      }
    },

    selectChild(child) {
      this.selectedChild = child;
    },

    async loadChildData() {
      if (!this.selectedChild) return;

      this.step = 2;
      this.loading = true;

      const data = {
        "function": "getChildGrowthHistory",
        "memberId": this.selectedChild.member_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.growthHistory = res.data.data.history || [];

          // Auto-populate referral if last status was SAM/MAM
          const lastRecord = this.growthHistory[0];
          if (lastRecord && (lastRecord.nutritional_status === 'SAM' ||
              lastRecord.nutritional_status === 'MAM')) {
            this.referralNeeded = true;
          }
        }
      } catch (e) {
        console.error("Failed to load growth history:", e);
      } finally {
        this.loading = false;
      }
    },

    previousStep() {
      this.step = 1;
      this.resetMeasurements();
    },

    resetMeasurements() {
      this.measurements = {
        weight: null,
        height: null,
        muac: null,
        edema: false
      };
      this.services = {
        vitaminA: false,
        deworming: false,
        counseling: false
      };
      this.referralNeeded = false;
    },

    validateWeight() {
      if (!this.measurements.weight) {
        this.weightError = 'Weight is required';
      } else if (this.measurements.weight < 0 || this.measurements.weight > 50) {
        this.weightError = 'Weight must be between 0 and 50 kg';
      } else {
        this.weightError = '';
      }
    },

    validateMUAC() {
      if (this.measurements.muac && (this.measurements.muac < 0 || this.measurements.muac > 30)) {
        this.muacError = 'MUAC must be between 0 and 30 cm';
      } else {
        this.muacError = '';
      }
    },

    async recordMeasurements() {
      // Validate
      this.validateWeight();
      this.validateMUAC();

      if (this.weightError || this.muacError || !this.measurements.weight) return;

      this.submitting = true;

      const data = {
        "function": "recordGrowthMonitoring",
        "memberId": this.selectedChild.member_id,
        "measurementDate": new Date().toISOString().split('T')[0],
        "weight": this.measurements.weight,
        "height": this.measurements.height,
        "muac": this.measurements.muac,
        "edema": this.measurements.edema,
        "vitaminAGiven": this.services.vitaminA,
        "dewormingGiven": this.services.deworming,
        "feedingCounseling": this.services.counseling
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Create referral if needed
          if (this.referralNeeded && res.data.data.referralNeeded) {
            this.$emit('referralNeeded', {
              child: this.selectedChild,
              nutritionalStatus: res.data.data.nutritionalStatus
            });
          }

          this.$emit('recorded', {
            childId: this.selectedChild.member_id,
            nutritionalStatus: res.data.data.nutritionalStatus
          });
        } else {
          alert(res.data.message || 'Failed to record measurements');
        }
      } catch (e) {
        console.error("Failed to record growth monitoring:", e);
        alert('Failed to record measurements. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    // Utility methods
    getChildAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
          (today.getMonth() - birthDate.getMonth());

      if (ageInMonths < 12) {
        return `${ageInMonths} months`;
      } else {
        const years = Math.floor(ageInMonths / 12);
        const months = ageInMonths % 12;
        return months > 0 ? `${years}y ${months}m` : `${years} years`;
      }
    },

    getAgeInMonths(dateOfBirth) {
      if (!dateOfBirth) return 0;

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      return (today.getFullYear() - birthDate.getFullYear()) * 12 +
          (today.getMonth() - birthDate.getMonth());
    },

    getDaysSince(dateString) {
      if (!dateString) return 0;
      const date = new Date(dateString);
      const today = new Date();
      const diffTime = Math.abs(today - date);
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getNutritionalStatusClass(status) {
      const classes = {
        'SAM': 'text-danger',
        'MAM': 'text-warning',
        'normal': 'text-success'
      };
      return classes[status] || 'text-muted';
    },

    getNutritionalBadgeClass(status) {
      const classes = {
        'SAM': 'bg-danger',
        'MAM': 'bg-warning',
        'normal': 'bg-success'
      };
      return classes[status] || 'bg-secondary';
    },

    getNutritionalIcon(status) {
      const icons = {
        'SAM': 'bx-error-circle',
        'MAM': 'bx-error',
        'normal': 'bx-check-circle'
      };
      return icons[status] || 'bx-question-mark';
    },

    getNutritionalStatusLabel(status) {
      const labels = {
        'SAM': 'Severe Acute Malnutrition',
        'MAM': 'Moderate Acute Malnutrition',
        'normal': 'Normal'
      };
      return labels[status] || status;
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #999;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #556ee6;
  border-color: #556ee6;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.table-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.invalid-feedback {
  display: block;
}
</style>